// Creative Library Page - COMPLETE IMPLEMENTATION (E007-S012)
import React, { useState, useEffect } from 'react';
import { creativeLibraryApi } from '../lib/creativeLibraryApi';
import type { Creative, CreativeFilters, ViewMode, PerformanceTier, CreativeStatus, CreativeType } from '../types/creative-library';
import { PerformanceTierBadge } from '../components/PerformanceTierBadge';
import { CreativeDetailModal } from '../components/modals/CreativeDetailModal';
import { ReuseCreativeModal } from '../components/modals/ReuseCreativeModal';
import { formatCurrency, formatNumber, formatPercent } from '../lib/formatters';

export const CreativeLibraryPage: React.FC = () => {
  // State
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedCreative, setSelectedCreative] = useState<Creative | null>(null);
  const [reuseCreative, setReuseCreative] = useState<Creative | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  // Filters
  const [filters, setFilters] = useState<CreativeFilters>({
    search: '',
    platforms: [],
    creative_types: [],
    performance_tiers: [],
    statuses: [],
    tags: [],
    date_range: { start: null, end: null },
    min_roas: undefined,
    max_roas: undefined,
    min_ctr: undefined,
    max_ctr: undefined,
    min_cpa: undefined,
    max_cpa: undefined
  });
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [total, setTotal] = useState(0);
  
  // Filter panel
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadCreatives();
  }, [filters, page, pageSize]);

  const loadCreatives = async () => {
    try {
      setLoading(true);
      const response = await creativeLibraryApi.list(filters, { page, page_size: pageSize });
      setCreatives(response.creatives);
      setTotal(response.total);
    } catch (error) {
      console.error('Failed to load creatives:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, search: e.target.value });
    setPage(1); // Reset to first page
  };

  const handlePlatformToggle = (platform: string) => {
    const platforms = filters.platforms?.includes(platform)
      ? filters.platforms.filter(p => p !== platform)
      : [...(filters.platforms || []), platform];
    setFilters({ ...filters, platforms });
    setPage(1);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === creatives.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(creatives.map(c => c.id)));
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkTag = async (tag: string) => {
    try {
      await creativeLibraryApi.bulkTag(Array.from(selectedIds), [tag]);
      await loadCreatives();
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to bulk tag:', error);
    }
  };

  const handleBulkArchive = async () => {
    if (!confirm(`Archive ${selectedIds.size} creatives?`)) return;
    try {
      await creativeLibraryApi.bulkArchive(Array.from(selectedIds));
      await loadCreatives();
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to bulk archive:', error);
    }
  };

  const handleBulkExport = async () => {
    try {
      await creativeLibraryApi.bulkExport(Array.from(selectedIds));
      alert('Export started! Check your downloads.');
    } catch (error) {
      console.error('Failed to bulk export:', error);
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-black dark:text-white">
            Creative Library
          </h1>
          <p className="text-bodydark dark:text-bodydark1 mt-2">
            {total.toLocaleString()} creatives across all platforms
          </p>
        </div>
        
        {/* View Mode Toggles */}
        <div className="flex items-center gap-2 bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'grid'
                ? 'bg-primary text-white'
                : 'text-bodydark hover:bg-gray-100 dark:hover:bg-meta-4'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'list'
                ? 'bg-primary text-white'
                : 'text-bodydark hover:bg-gray-100 dark:hover:bg-meta-4'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('detailed')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'detailed'
                ? 'bg-primary text-white'
                : 'text-bodydark hover:bg-gray-100 dark:hover:bg-meta-4'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark mb-6">
        <div className="p-4 flex items-center gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <svg
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-bodydark dark:text-bodydark1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={filters.search || ''}
              onChange={handleSearch}
              placeholder="Search creatives..."
              data-testid="search-input"
              className="w-full pl-12 pr-4 py-3 border border-stroke dark:border-strokedark rounded-lg bg-transparent"
            />
          </div>

          {/* Platform Quick Filters */}
          <div className="flex items-center gap-2">
            {['meta', 'google', 'tiktok'].map(platform => (
              <button
                key={platform}
                data-testid={`filter-platform-${platform}`}
                onClick={() => handlePlatformToggle(platform)}
                className={`px-4 py-2 rounded-lg border transition-colors capitalize ${
                  filters.platforms?.includes(platform)
                    ? 'bg-primary text-white border-primary'
                    : 'border-stroke dark:border-strokedark hover:border-primary'
                }`}
              >
                {platform}
              </button>
            ))}
          </div>

          {/* Advanced Filters Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 rounded-lg border border-stroke dark:border-strokedark hover:border-primary transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
            Filters
            {Object.values(filters).filter(v => v && v.length > 0).length > 0 && (
              <span className="px-2 py-0.5 bg-primary text-white rounded-full text-xs">
                {Object.values(filters).filter(v => v && v.length > 0).length}
              </span>
            )}
          </button>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="border-t border-stroke dark:border-strokedark p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Creative Type */}
            <div>
              <label className="block text-sm font-medium mb-2">Creative Type</label>
              <select
                multiple
                className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-transparent h-24"
                value={filters.creative_types || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value) as CreativeType[];
                  setFilters({ ...filters, creative_types: selected });
                }}
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="carousel">Carousel</option>
                <option value="collection">Collection</option>
              </select>
            </div>

            {/* Performance Tier */}
            <div>
              <label className="block text-sm font-medium mb-2">Performance Tier</label>
              <select
                multiple
                className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-transparent h-24"
                value={filters.performance_tiers || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value) as PerformanceTier[];
                  setFilters({ ...filters, performance_tiers: selected });
                }}
              >
                <option value="top">🏆 Top 10%</option>
                <option value="above_average">⭐ Above Average</option>
                <option value="average">📊 Average</option>
                <option value="below_average">⚠️ Below Average</option>
                <option value="poor">🔴 Poor</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select
                multiple
                className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-transparent h-24"
                value={filters.statuses || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value) as CreativeStatus[];
                  setFilters({ ...filters, statuses: selected });
                }}
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* ROAS Range */}
            <div>
              <label className="block text-sm font-medium mb-2">ROAS Range</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="Min"
                  value={filters.min_roas || ''}
                  onChange={(e) => setFilters({ ...filters, min_roas: e.target.value ? parseFloat(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-transparent"
                />
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="Max"
                  value={filters.max_roas || ''}
                  onChange={(e) => setFilters({ ...filters, max_roas: e.target.value ? parseFloat(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-transparent"
                />
              </div>
            </div>

            {/* CTR Range */}
            <div>
              <label className="block text-sm font-medium mb-2">CTR Range (%)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="Min"
                  value={filters.min_ctr || ''}
                  onChange={(e) => setFilters({ ...filters, min_ctr: e.target.value ? parseFloat(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-transparent"
                />
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="Max"
                  value={filters.max_ctr || ''}
                  onChange={(e) => setFilters({ ...filters, max_ctr: e.target.value ? parseFloat(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-transparent"
                />
              </div>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters({
                    search: '',
                    platforms: [],
                    creative_types: [],
                    performance_tiers: [],
                    statuses: [],
                    tags: [],
                    date_range: { start: null, end: null },
                    min_roas: undefined,
                    max_roas: undefined,
                    min_ctr: undefined,
                    max_ctr: undefined,
                    min_cpa: undefined,
                    max_cpa: undefined
                  });
                  setPage(1);
                }}
                className="w-full px-4 py-2 border border-danger text-danger rounded-lg hover:bg-danger hover:text-white transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-primary bg-opacity-10 border border-primary rounded-lg p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-medium text-primary">
              {selectedIds.size} selected
            </span>
            <button
              onClick={handleSelectAll}
              className="text-sm text-primary hover:underline"
            >
              {selectedIds.size === creatives.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const tag = prompt('Enter tag name:');
                if (tag) handleBulkTag(tag);
              }}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 transition-colors"
            >
              Add Tag
            </button>
            <button
              onClick={handleBulkExport}
              className="px-4 py-2 border border-stroke dark:border-strokedark rounded-lg hover:border-primary transition-colors"
            >
              Export
            </button>
            <button
              onClick={handleBulkArchive}
              className="px-4 py-2 border border-danger text-danger rounded-lg hover:bg-danger hover:text-white transition-colors"
            >
              Archive
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-bodydark dark:text-bodydark1 mt-4">Loading creatives...</p>
        </div>
      )}

      {/* Grid View */}
      {!loading && viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
          {creatives.map(creative => (
            <div
              key={creative.id}
              data-testid="creative-card"
              className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark hover:shadow-lg transition-shadow cursor-pointer group relative"
            >
              {/* Checkbox */}
              <div className="absolute top-3 left-3 z-10">
                <input
                  type="checkbox"
                  checked={selectedIds.has(creative.id)}
                  onChange={() => handleSelectOne(creative.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-5 h-5 rounded border-2 border-stroke dark:border-strokedark"
                />
              </div>

              {/* Image */}
              <div
                onClick={() => setSelectedCreative(creative)}
                className="aspect-square bg-gray-100 dark:bg-meta-4 rounded-t-lg overflow-hidden"
              >
                {creative.thumbnail_url || creative.asset_url ? (
                  <img
                    src={creative.thumbnail_url || creative.asset_url}
                    alt={creative.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4" onClick={() => setSelectedCreative(creative)}>
                <div className="flex items-center justify-between mb-2">
                  <span className="px-2 py-1 bg-primary bg-opacity-10 text-primary rounded text-xs font-medium capitalize">
                    {creative.platform}
                  </span>
                  <PerformanceTierBadge
                    tier={creative.performance_tier}
                    roas={creative.roas}
                    totalCreatives={total}
                    iconOnly
                  />
                </div>
                <h3 className="font-semibold text-black dark:text-white mb-1 truncate">
                  {creative.name}
                </h3>
                <div className="text-sm text-bodydark dark:text-bodydark1 mb-3">
                  ROAS: {creative.roas.toFixed(2)}x • {formatNumber(creative.impressions)} imp
                </div>
                
                {/* Tags */}
                {creative.tags && creative.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {creative.tags.slice(0, 2).map(tag => (
                      <span key={tag} className="px-2 py-0.5 bg-bodydark1 bg-opacity-10 text-bodydark1 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                    {creative.tags.length > 2 && (
                      <span className="px-2 py-0.5 bg-bodydark1 bg-opacity-10 text-bodydark1 rounded text-xs">
                        +{creative.tags.length - 2}
                      </span>
                    )}
                  </div>
                )}

                {/* Quick Actions */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setReuseCreative(creative);
                  }}
                  className="w-full px-3 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 transition-colors text-sm font-medium"
                >
                  Reuse in Campaign
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {!loading && viewMode === 'list' && (
        <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark mb-6 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-meta-4">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === creatives.length}
                    onChange={handleSelectAll}
                    className="w-5 h-5 rounded border-2"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">Preview</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Platform</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Performance</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Metrics</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {creatives.map(creative => (
                <tr key={creative.id} className="border-t border-stroke dark:border-strokedark hover:bg-gray-50 dark:hover:bg-meta-4">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(creative.id)}
                      onChange={() => handleSelectOne(creative.id)}
                      className="w-5 h-5 rounded border-2"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <img
                      src={creative.thumbnail_url || creative.asset_url}
                      alt={creative.name}
                      className="w-16 h-16 object-cover rounded cursor-pointer"
                      onClick={() => setSelectedCreative(creative)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div 
                      className="font-medium text-black dark:text-white cursor-pointer hover:text-primary"
                      onClick={() => setSelectedCreative(creative)}
                    >
                      {creative.name}
                    </div>
                    <div className="text-sm text-bodydark dark:text-bodydark1 capitalize">
                      {creative.creative_type}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-3 py-1 bg-primary bg-opacity-10 text-primary rounded text-sm font-medium capitalize">
                      {creative.platform}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <PerformanceTierBadge
                      tier={creative.performance_tier}
                      roas={creative.roas}
                      totalCreatives={total}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div>ROAS: {creative.roas.toFixed(2)}x</div>
                    <div className="text-bodydark dark:text-bodydark1">
                      {formatNumber(creative.impressions)} imp • {formatPercent(creative.ctr)} CTR
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setReuseCreative(creative)}
                      className="px-3 py-1 bg-primary text-white rounded hover:bg-opacity-90 transition-colors text-sm"
                    >
                      Reuse
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!loading && creatives.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-medium text-black dark:text-white mb-2">No creatives found</h3>
          <p className="text-bodydark dark:text-bodydark1">
            Try adjusting your filters or search terms
          </p>
        </div>
      )}

      {/* Pagination */}
      {!loading && total > pageSize && (
        <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4 flex items-center justify-between">
          <div className="text-sm text-bodydark dark:text-bodydark1">
            Showing {startItem}-{endItem} of {total.toLocaleString()} creatives
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-stroke dark:border-strokedark rounded-lg hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                
                return (
                  <button
                    key={i}
                    onClick={() => setPage(pageNum)}
                    className={`w-10 h-10 rounded-lg transition-colors ${
                      page === pageNum
                        ? 'bg-primary text-white'
                        : 'border border-stroke dark:border-strokedark hover:border-primary'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-stroke dark:border-strokedark rounded-lg hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>

          {/* Page Size Selector */}
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setPage(1);
            }}
            className="px-3 py-2 border border-stroke dark:border-strokedark rounded-lg bg-transparent"
          >
            <option value="12">12 per page</option>
            <option value="24">24 per page</option>
            <option value="48">48 per page</option>
            <option value="96">96 per page</option>
          </select>
        </div>
      )}

      {/* Modals */}
      {selectedCreative && (
        <CreativeDetailModal
          creativeId={selectedCreative.id}
          onClose={() => setSelectedCreative(null)}
          onReuse={(id) => {
            setSelectedCreative(null);
            const creative = creatives.find(c => c.id === id);
            if (creative) setReuseCreative(creative);
          }}
          onArchive={(id) => {
            setSelectedCreative(null);
            loadCreatives();
          }}
        />
      )}

      {reuseCreative && (
        <ReuseCreativeModal
          creative={reuseCreative}
          onClose={() => setReuseCreative(null)}
          onSuccess={(campaignId, adId) => {
            setReuseCreative(null);
            alert(`Creative added to campaign successfully!`);
          }}
        />
      )}
    </div>
  );
};
